import Dialog from "@mui/material/Dialog"
import {
  BankUploadMessage,
  DrawerContent,
  PrimaryButton,
  StyledButton,
  StyledFormControl,
  UploadAreaBox,
  UploadBox,
} from "./styles"
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import UploadFileIcon from "@mui/icons-material/UploadFile"
import { useEffect, useState } from "react"
import { Bank } from "../../models/transactions.model"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import {
  selectTransactions,
  uploadCSVFile,
} from "../../slices/transactionsSlice"
import {
  fetchUploadlog,
  selectUploadHistory,
} from "../../slices/uploadHistorySlice"
import dayjs from "dayjs"
import { INDIAN_STANDARD_DATE_TIME } from "../../constants/format"

type UploadPopupProps = {
  open: boolean
  onClose: () => void
  bankList: Bank[]
}

const UploadPopup = ({ open, onClose, bankList }: UploadPopupProps) => {
  const dispatch = useAppDispatch()
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadInitiated, setUploadInitiated] = useState(false)

  const [isDragging, setIsDragging] = useState(false)

  const { uploadLog } = useAppSelector(selectUploadHistory)
  const { isUploading } = useAppSelector(selectTransactions)

  useEffect(() => {
    dispatch(
      fetchUploadlog(
        selectedBank?.id ? { bankAccountId: selectedBank.id } : {},
      ),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBank?.id])

  useEffect(() => {
    if (uploadInitiated && !isUploading) {
      setSelectedFile(null)
      setSelectedBank(null)
      setUploadInitiated(false)
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadInitiated, isUploading])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)

    const droppedFile = event.dataTransfer.files && event.dataTransfer.files[0]
    if (droppedFile && selectedBank) {
      setSelectedFile(droppedFile)
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
  }

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null)
      setSelectedBank(null)
      setUploadInitiated(false)
      onClose()
    }
  }

  const handleSubmit = async () => {
    if (selectedFile && selectedBank) {
      setUploadInitiated(true)
      dispatch(
        uploadCSVFile({
          file: selectedFile,
          accountId: selectedBank.id,
        }),
      )
    }
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 10) / 10 + " " + sizes[i]
  }

  const getFileExtension = (filename: string) => {
    const ext = filename.split(".").pop()?.toUpperCase()
    return ext || "FILE"
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle sx={{ m: 0, pb: 0, fontWeight: 700 }}>
        Upload Bank Statement
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={(theme) => ({
          position: "absolute",
          right: 4,
          top: 4,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <DrawerContent>
        <DialogContentText px={"2px"} fontSize={"14px"} fontWeight={600}>
          Add your documents here, and you can upload up to 1 file max
        </DialogContentText>
        {uploadLog.bankAccountId ? (
          <BankUploadMessage status={uploadLog.status}>
            Last statement was uploaded by <span>{uploadLog?.uploadedBy}</span>{" "}
            and bank account of <span>{uploadLog.accountName}</span> at{" "}
            <span>
              {dayjs(uploadLog?.uploadedAt).format(INDIAN_STANDARD_DATE_TIME) ||
                "-"}
            </span>
          </BankUploadMessage>
        ) : (
          <BankUploadMessage>No Bank statements uploaded yet</BankUploadMessage>
        )}
        <StyledFormControl size="small">
          <Autocomplete
            fullWidth
            id="select-banks-account"
            options={bankList}
            value={selectedBank}
            onChange={(_, newValue) => {
              setSelectedBank(newValue)
            }}
            getOptionLabel={(option: Bank) => option.accountName}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Account"
                variant="outlined"
                size="small"
              />
            )}
          />
        </StyledFormControl>

        {selectedFile ? (
          <Box
            sx={{
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: 2,
              padding: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              backgroundColor: "background.paper",
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                backgroundColor: "#E3F2FD",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "#1976D2",
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                {getFileExtension(selectedFile.name)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selectedFile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(selectedFile.size)}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleRemoveFile}
              sx={{
                color: "error.main",
                flexShrink: 0,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <UploadBox
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            isDragging={isDragging}
            selectedBank={!!selectedBank}
          >
            <UploadAreaBox>
              <UploadFileIcon sx={{ fontSize: 40, color: "#F59E0B" }} />
            </UploadAreaBox>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Drag your CSV file to start uploading
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              OR
            </Typography>
            <input
              accept={selectedBank?.allowedFileType || ".csv, .xls, .xlsx"}
              style={{ display: "none" }}
              id="contained-button-file"
              type="file"
              onChange={handleFileChange}
              onClick={(
                event: React.MouseEvent<HTMLInputElement, MouseEvent>,
              ) => {
                const element = event.target as HTMLInputElement
                element.value = ""
              }}
            />
            <label htmlFor="contained-button-file">
              <Button
                variant="outlined"
                component="span"
                disabled={!selectedBank || selectedBank.id === ""}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 3,
                }}
              >
                Browse files
              </Button>
            </label>
          </UploadBox>
        )}

        <Box sx={{ display: "flex", gap: "10px", flex: 1 }}>
          <StyledButton
            sx={{ width: "100%" }}
            variant="outlined"
            color="inherit"
            onClick={handleClose}
          >
            Cancel
          </StyledButton>
          <PrimaryButton
            sx={{ width: "100%" }}
            variant="contained"
            onClick={handleSubmit}
            disabled={!selectedFile || !selectedBank || isUploading}
          >
            {isUploading ? (
              <CircularProgress size={22} color="secondary" />
            ) : (
              "Submit"
            )}
          </PrimaryButton>
        </Box>
      </DrawerContent>
    </Dialog>
  )
}

export default UploadPopup
